import * as XLSX from 'xlsx';
import { jobState } from '../ProfilePage/calendar/calendarDates';

// Çalışanların takvim işleri (CalendarJobs): tarih kuralları ve "Viziti Olmayan
// Çalışanlar" raporu. Excel başlıkları Türkçe kalır.

/** First and last day of a job ('yyyy-mm-dd'). */
export const jobDays = (job) => {
  const start = String(job.startDate || '');
  return { start, end: String(job.endDate || start) };
};

/** Does the job touch the period? An empty limit means no limit. */
export const isJobInRange = (job, startDate, endDate) => {
  const { start, end } = jobDays(job);
  return (!startDate || end >= startDate) && (!endDate || start <= endDate);
};

const isStillAssigned = (worker) => (worker.projects || []).some((project) => project.status === 'active' && !project.isArchived);

const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : null);

/**
 * Seçilen dönemde viziti olmayan çalışanlar ve takvim işlerinin özeti. Ekip
 * Performans Analizi yalnızca viziti olanları gösterdiği için her çalışan
 * ikisinden birinde yer alır. `visits` döneme göre süzülmüş olarak gelir.
 * Merkezden ayrılmış çalışan yalnızca o dönemde iş girdiyse listelenir.
 */
export const buildNoVisitReport = ({ workers, visits, jobsByWorker, startDate, endDate }) => {
  const workersWithVisit = new Set(visits.map((visit) => visit.employeeId));

  const rows = workers
    .filter((worker) => !workersWithVisit.has(worker.id))
    .map((worker) => {
      const jobs = (jobsByWorker[worker.id] || []).filter((job) => isJobInRange(job, startDate, endDate));
      const days = jobs.map((job) => jobDays(job).start).filter(Boolean);
      const done = jobs.filter((job) => job.status).length;
      return {
        id: worker.id,
        worker,
        name: `${worker.name} ${worker.surname}`.trim(),
        roles: [...new Set((worker.projects || []).map((project) => project.role).filter(Boolean))],
        jobs,
        total: jobs.length,
        done,
        open: jobs.length - done,
        overdue: jobs.filter((job) => jobState(job) === 'overdue').length,
        donePct: percent(done, jobs.length),
        activeDays: new Set(days).size,
        lastDay: days.reduce((latest, day) => (day > latest ? day : latest), ''),
      };
    })
    .filter((row) => row.total > 0 || isStillAssigned(row.worker))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'tr'));

  const sum = (key) => rows.reduce((total, row) => total + row[key], 0);
  return {
    rows,
    totals: {
      workers: rows.length,
      jobs: sum('total'),
      overdue: sum('overdue'),
      donePct: percent(sum('done'), sum('total')),
      withoutJobs: rows.filter((row) => row.total === 0).length,
    },
  };
};

const excelDay = (day) => {
  const date = new Date(`${day}T00:00:00`);
  return Number.isNaN(date.getTime()) ? day : date.toLocaleDateString('tr-TR');
};

/** Excel: per-worker summary, then every job one by one. */
export const exportNoVisitExcel = (report, smoName) => {
  const summaryRows = report.rows.map((row) => ({
    'Çalışan': row.name,
    'Rol': row.roles.join(', '),
    'İş Girişi': row.total,
    'Tamamlanan': row.done,
    'Devam Eden': row.open,
    'Gecikmiş': row.overdue,
    'Tamamlanma %': row.donePct ?? '',
    'Aktif Gün': row.activeDays,
    'Son İş Tarihi': row.lastDay ? excelDay(row.lastDay) : '',
  }));
  const jobRows = report.rows.flatMap((row) => row.jobs.map((job) => {
    const { start, end } = jobDays(job);
    return {
      'Çalışan': row.name,
      'Başlangıç': start ? excelDay(start) : '',
      'Bitiş': end ? excelDay(end) : '',
      'Saat': job.time || '',
      'İş': job.title || '',
      'Proje': job.projectName || '',
      'Durum': job.status ? 'Tamamlandı' : 'Devam Ediyor',
    };
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), 'Çalışanlar');
  if (jobRows.length > 0) XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(jobRows), 'İşler');
  XLSX.writeFile(workbook, `${smoName}_Viziti_Olmayan_Calisanlar.xlsx`);
};
